/**
 * Small formatting helpers for the portal. Pure, so they are the cheapest thing here to test.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Initials for the person circle. **Not avatars** — `AppUser` has no photo column and nothing
 * in Tunet stores an image of a person. plan/portal-design.md says to call these "initials"
 * precisely so nobody goes looking for the image field.
 *
 * Falls back through display name, then the address, then a dash: all three can be a string this
 * function has no letters to work with, and a blank circle reads as a rendering bug rather than
 * as missing data.
 */
export function initialsOf(displayName: string, fallback: string): string {
  const source = displayName.trim() || fallback.trim();
  const words = source.split(/\s+/).filter((word) => word.length > 0);

  if (words.length === 0) {
    return '—';
  }

  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}

/**
 * A relative time, coarse on purpose, and **the portal's most-used kind of context**.
 *
 * plan/portal-design.md restates "no number without a comparison" as "no number without
 * CONTEXT" — a delta, a denominator, a recency, or a breakdown — because almost nothing in
 * Tunet stores a prior value to compare against. A recency carries direction and magnitude
 * without needing one, which is exactly why the room table leans on it: `RoomActivity` is
 * upserted, one row per (Room, User), so its timestamp has no history behind it and never will.
 *
 * `null` in, "never" out — and that word is doing real work rather than standing in for zero.
 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (iso === null || iso === undefined) {
    return 'never';
  }

  const then = new Date(iso);

  if (Number.isNaN(then.getTime())) {
    return 'unknown';
  }

  const elapsed = now.getTime() - then.getTime();

  if (elapsed < 0) {
    return 'just now';
  }

  const minutes = Math.floor(elapsed / 60_000);

  if (minutes < 1) {
    return 'just now';
  }

  if (minutes < 60) {
    return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  }

  const days = Math.floor(elapsed / MS_PER_DAY);

  if (days === 1) {
    return 'yesterday';
  }

  if (days < 30) {
    return `${days} days ago`;
  }

  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? 'a month ago' : `${months} months ago`;
  }

  const years = Math.floor(days / 365);

  return years === 1 ? 'a year ago' : `${years} years ago`;
}

/**
 * How long until a timestamp in the FUTURE — an expiry, and today only a share link's or a
 * grant's.
 *
 * **This exists because {@link relativeTime} is past-only and fails silently rather than
 * loudly.** Its very first branch turns a negative elapsed time into "just now", which is right
 * for a clock skew of a few seconds on a `createdAt` and catastrophically wrong for an
 * `expiresAt`: a share link with six days left rendered as "expires just now", which reads as
 * "this is dying as you look at it". Caught by rendering the panel and looking at it, not by any
 * test — nothing in the type system distinguishes a past instant from a future one, so the only
 * defence is having two functions and picking the right one.
 *
 * Deliberately not a `future` flag on {@link relativeTime}: a boolean deciding which direction a
 * duration is read in is one inverted argument away from the same bug, whereas calling the wrong
 * function produces a sentence that is obviously wrong the moment anybody sees it.
 */
export function untilTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (iso === null || iso === undefined) {
    return 'never';
  }

  const then = new Date(iso);

  if (Number.isNaN(then.getTime())) {
    return 'unknown';
  }

  const remaining = then.getTime() - now.getTime();

  // Past, which a caller should normally have handled before reaching here — a link whose expiry
  // has gone is rendered as expired, not as expiring. Answered honestly rather than as a
  // duration, so a caller that gets this wrong reads wrong instead of reading plausible.
  if (remaining <= 0) {
    return 'in the past';
  }

  const minutes = Math.floor(remaining / 60_000);

  if (minutes < 1) {
    return 'in under a minute';
  }

  if (minutes < 60) {
    return minutes === 1 ? 'in a minute' : `in ${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return hours === 1 ? 'in an hour' : `in ${hours} hours`;
  }

  const days = Math.floor(remaining / MS_PER_DAY);

  if (days === 1) {
    return 'tomorrow';
  }

  if (days < 30) {
    return `in ${days} days`;
  }

  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? 'in a month' : `in ${months} months`;
  }

  const years = Math.floor(days / 365);

  return years === 1 ? 'in a year' : `in ${years} years`;
}

/** Whether a timestamp falls inside the last `days`. `null` — never — is not "recent". */
export function isWithinDays(iso: string | null | undefined, days: number, now: Date = new Date()): boolean {
  if (iso === null || iso === undefined) {
    return false;
  }

  const at = new Date(iso).getTime();

  return !Number.isNaN(at) && at >= now.getTime() - days * MS_PER_DAY;
}

export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}
